import { useState, useContext, useEffect } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import de from "date-fns/locale/de";
import { format } from "date-fns";
import { NameContext } from "../context/NameContext";

registerLocale("de", de);

// ICS-Generator für Download-Button
function generateICS({ name, date, time, summary, email }) {
  const dt = new Date(`${date}T${time}:00`);
  const pad = n => n.toString().padStart(2, "0");
  const toUTC = d =>
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`;

  const dtStart = toUTC(dt);
  const dtEnd = toUTC(new Date(dt.getTime() + 60 * 60 * 1000));
  const uid = `${Math.random().toString(36).slice(2)}@denisbitter.de`;
  const esc = s => (s ? String(s).replace(/(\r\n|\r|\n)/g, " ") : "");

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Denis Bitter Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:REQUEST",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `SUMMARY:${esc(summary)}`,
    `DTSTART:${dtStart}`,
    `DTEND:${dtEnd}`,
    `DTSTAMP:${dtStart}`,
    `ORGANIZER;CN=Denis Bitter:mailto:saghallo@denisbitter.de`,
    email
      ? `ATTENDEE;CN=${esc(name)};RSVP=TRUE:mailto:${email}`
      : "",
    `DESCRIPTION:Online-Termin mit ${esc(name)} per Teams`,
    "SEQUENCE:0",
    "STATUS:CONFIRMED",
    "TRANSP:OPAQUE",
    "END:VEVENT",
    "END:VCALENDAR"
  ]
    .filter(Boolean)
    .join("\r\n");
}

function downloadICS({ name, date, time, summary, email }) {
  const ics = generateICS({ name, date, time, summary, email });
  const blob = new Blob([ics], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "Termin mit Denis Bitter.ics";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

const inputClass =
  "flex-1 bg-transparent text-sm text-[#EFEBDC] placeholder-[#EFEBDC]/20 outline-none border-none ring-0 focus:outline-none focus:ring-0 focus:border-none caret-[#AC8E66] font-mono";

function BookingModal({ onClose }) {
  const [step, setStep] = useState("date");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [consent, setConsent] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { name } = useContext(NameContext);

  const timeSlots = [
    "09:00", "09:30", "10:00", "10:30",
    "11:00", "11:30", "15:00", "16:30",
  ];

  // Scroll lock + ESC
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.classList.add("modal-open");
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!consent) return;
    setIsLoading(true);
    setError(null);

    const payload = {
      name: formData.name,
      email: formData.email,
      date: selectedDate ? format(selectedDate, "yyyy-MM-dd") : "",
      time: selectedTime,
      summary:
        selectedDate && selectedTime
          ? `${format(selectedDate, "dd.MM.yyyy", { locale: de })} um ${selectedTime} per Teams`
          : "",
    };

    try {
      const response = await fetch("https://www.denisbitter.de/stage01/api/booking_mail.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.message || "Versand nicht erfolgreich.");
      }
    } catch (err) {
      setError("Es gab einen technischen Fehler beim Senden.");
      console.error(err);
    }
    setIsLoading(false);
  };

  const bookingSummary =
    selectedDate && selectedTime
      ? `${format(selectedDate, "dd.MM.yyyy", { locale: de })} um ${selectedTime} per Teams`
      : "";

  // Step label for title bar
  const stepLabel = { date: "1/3", time: "2/3", form: "3/3" };

  let content;
  if (success) {
    content = (
      <div className="space-y-2">
        <p className="text-sm text-[#EFEBDC]/50">
          <span className="text-[#AC8E66]">$</span> book --confirm
        </p>
        <p className="text-sm text-green-400">
          <span className="text-green-400/60">&gt;</span> buchung bestaetigt.
        </p>
        <p className="text-sm text-[#EFEBDC]/60">
          <span className="text-[#AC8E66]/80">&gt;</span> {bookingSummary}
        </p>
        <p className="text-sm text-[#EFEBDC]/40">
          <span className="text-[#AC8E66]/80">&gt;</span> eine email ist unterwegs zu dir.
        </p>
        <div className="flex flex-col gap-2 pt-4 border-t border-[#AC8E66]/10 mt-4">
          <button
            onClick={() =>
              downloadICS({
                name: formData.name,
                email: formData.email,
                date: selectedDate ? format(selectedDate, "yyyy-MM-dd") : "",
                time: selectedTime,
                summary: bookingSummary,
              })
            }
            className="w-full py-2 text-xs text-[#EFEBDC]/70 bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors cursor-pointer font-mono"
          >
            <span className="text-[#AC8E66]">$</span> save --calendar
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 text-xs text-[#EFEBDC]/40 hover:text-[#EFEBDC]/60 transition-colors cursor-pointer font-mono"
          >
            <span className="text-[#AC8E66]/40">[</span>close<span className="text-[#AC8E66]/40">]</span>
          </button>
        </div>
      </div>
    );
  } else if (step === "date") {
    content = (
      <>
        <p className="text-sm text-[#EFEBDC]/50 mb-2">
          <span className="text-[#AC8E66]">$</span> book --meeting {name && <span className="text-[#AC8E66]">--user {name}</span>}
        </p>
        <p className="text-xs text-[#EFEBDC]/40 mb-4">
          <span className="text-[#AC8E66]/80">&gt;</span> waehle einen tag:
        </p>
        <div className="flex justify-center booking-datepicker-dark">
          <DatePicker
            selected={selectedDate}
            onChange={date => setSelectedDate(date)}
            minDate={new Date()}
            locale="de"
            inline
          />
        </div>
        {selectedDate && (
          <p className="text-xs text-green-400/70 mt-3">
            <span className="text-green-400/40">&gt;</span> {format(selectedDate, "dd.MM.yyyy", { locale: de })}
          </p>
        )}
        <div className="flex flex-col gap-2 mt-4 pt-3 border-t border-[#AC8E66]/10">
          <button
            onClick={() => selectedDate && setStep("time")}
            className={`w-full py-2 text-xs text-[#EFEBDC]/70 bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors font-mono ${
              !selectedDate ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <span className="text-[#AC8E66]/40">[</span>weiter<span className="text-[#AC8E66]/40">]</span>
          </button>
          <button
            onClick={onClose}
            className="w-full py-1.5 text-xs text-[#EFEBDC]/30 hover:text-[#EFEBDC]/50 transition-colors cursor-pointer font-mono"
          >
            <span className="text-[#AC8E66]/30">[</span>abbrechen<span className="text-[#AC8E66]/30">]</span>
          </button>
        </div>
      </>
    );
  } else if (step === "time") {
    content = (
      <>
        <p className="text-sm text-[#EFEBDC]/50 mb-2">
          <span className="text-[#AC8E66]">$</span> select --time
        </p>
        <p className="text-xs text-[#EFEBDC]/40 mb-4">
          <span className="text-[#AC8E66]/80">&gt;</span> verfuegbare slots fuer {selectedDate && format(selectedDate, "dd.MM.yyyy", { locale: de })}:
        </p>
        <div className="grid grid-cols-4 gap-2">
          {timeSlots.map(slot => (
            <button
              key={slot}
              onClick={() => setSelectedTime(slot)}
              className={`py-2 text-xs font-mono rounded border transition-all cursor-pointer ${
                selectedTime === slot
                  ? "bg-[#AC8E66]/20 border-[#AC8E66]/60 text-[#AC8E66]"
                  : "border-[#AC8E66]/10 text-[#EFEBDC]/50 hover:border-[#AC8E66]/30 hover:text-[#EFEBDC]/70"
              }`}
            >
              {slot}
            </button>
          ))}
        </div>
        {selectedTime && (
          <p className="text-xs text-green-400/70 mt-3">
            <span className="text-green-400/40">&gt;</span> {format(selectedDate, "dd.MM.yyyy", { locale: de })} um {selectedTime}
          </p>
        )}
        <div className="flex flex-col gap-2 mt-4 pt-3 border-t border-[#AC8E66]/10">
          <button
            onClick={() => selectedTime && setStep("form")}
            className={`w-full py-2 text-xs text-[#EFEBDC]/70 bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors font-mono ${
              !selectedTime ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
            }`}
          >
            <span className="text-[#AC8E66]/40">[</span>weiter<span className="text-[#AC8E66]/40">]</span>
          </button>
          <button
            onClick={() => setStep("date")}
            className="w-full py-1.5 text-xs text-[#EFEBDC]/30 hover:text-[#EFEBDC]/50 transition-colors cursor-pointer font-mono"
          >
            <span className="text-[#AC8E66]/30">[</span>zurueck<span className="text-[#AC8E66]/30">]</span>
          </button>
        </div>
      </>
    );
  } else if (step === "form") {
    content = (
      <form onSubmit={handleSubmit}>
        <p className="text-sm text-[#EFEBDC]/50 mb-2">
          <span className="text-[#AC8E66]">$</span> confirm --booking
        </p>
        <p className="text-xs text-[#EFEBDC]/40 mb-4">
          <span className="text-[#AC8E66]/80">&gt;</span> termin: <span className="text-[#AC8E66]/70">{bookingSummary}</span>
        </p>

        {/* Name */}
        <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80 text-xs">&gt;</span>
          <span className="text-xs text-[#EFEBDC]/40 w-12 shrink-0">name</span>
          <input
            type="text"
            name="name"
            placeholder={name || "dein name"}
            required
            value={formData.name}
            onChange={handleInputChange}
            className={inputClass}
          />
        </div>

        {/* Email */}
        <div className="flex items-center gap-2 py-2 border-b border-[#AC8E66]/10">
          <span className="text-[#AC8E66]/80 text-xs">&gt;</span>
          <span className="text-xs text-[#EFEBDC]/40 w-12 shrink-0">email</span>
          <input
            type="email"
            name="email"
            placeholder="deine@email.de"
            required
            value={formData.email}
            onChange={handleInputChange}
            className={inputClass}
          />
        </div>

        {/* Datenschutz */}
        <div className="flex items-center gap-2 py-3">
          <span className="text-[#AC8E66]/80 text-xs">&gt;</span>
          <span className="text-xs text-[#EFEBDC]/40">datenschutz</span>
          <label className="inline-flex items-center cursor-pointer ml-auto">
            <input
              type="checkbox"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
              className="sr-only"
              required
            />
            <div className={`w-8 h-4 rounded-full relative transition ${consent ? "bg-green-500/30" : "bg-[#EFEBDC]/10"}`}>
              <div className={`w-3 h-3 rounded-full absolute top-0.5 left-0.5 transition-transform ${consent ? "translate-x-4 bg-green-400" : "bg-[#EFEBDC]/40"}`} />
            </div>
          </label>
        </div>
        <p className="text-xs text-[#EFEBDC]/25 ml-6 -mt-1 mb-3">
          gemaess{" "}
          <a href="/datenschutz" className="underline text-[#AC8E66]/50 hover:text-[#AC8E66]" target="_blank" rel="noopener noreferrer">
            datenschutz
          </a>
        </p>

        {error && (
          <p className="text-xs text-orange-400 mb-3">
            <span className="text-orange-400/60">&gt;</span> error: {error.toLowerCase()}
          </p>
        )}

        <div className="flex flex-col gap-2 pt-3 border-t border-[#AC8E66]/10">
          <button
            type="submit"
            disabled={isLoading || !consent}
            className={`w-full py-2 text-xs text-[#EFEBDC]/70 bg-[#AC8E66]/15 border border-[#AC8E66]/20 rounded hover:bg-[#AC8E66]/25 transition-colors font-mono ${
              (!consent || isLoading) ? "opacity-30 cursor-not-allowed" : "cursor-pointer"
            } ${isLoading ? "animate-pulse" : ""}`}
          >
            <span className="text-[#AC8E66]">$</span> {isLoading ? "sende..." : "book --send"}
          </button>
          <button
            type="button"
            onClick={() => setStep("time")}
            className="w-full py-1.5 text-xs text-[#EFEBDC]/30 hover:text-[#EFEBDC]/50 transition-colors cursor-pointer font-mono"
          >
            <span className="text-[#AC8E66]/30">[</span>zurueck<span className="text-[#AC8E66]/30">]</span>
          </button>
        </div>
      </form>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{ zIndex: 999999 }}
      className="fixed inset-0 bg-[#121212]/95 backdrop-blur-sm overflow-y-auto overscroll-contain flex items-start justify-center pt-12 sm:pt-20 pb-12 px-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="w-full max-w-md rounded-lg border border-[#AC8E66]/20 overflow-hidden shadow-2xl shadow-black/60 font-mono">

        {/* Title Bar */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1a1a] border-b border-[#AC8E66]/10">
          <span className="w-3 h-3 rounded-full bg-[#ff5f57] cursor-pointer" onClick={onClose} />
          <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
          <span className="w-3 h-3 rounded-full bg-[#28c840]" />
          <span className="ml-3 text-xs text-[#EFEBDC]/40 select-none">booking</span>
          {!success && (
            <span className="ml-auto text-xs text-[#EFEBDC]/25 select-none">
              {stepLabel[step]}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="px-4 sm:px-6 py-5 bg-[#0F0F0F]">
          {content}
        </div>
      </div>
    </div>
  );
}

export default BookingModal;
